import { StatsData, useGetStats } from "hooks/LabelPropagation/useGetStats";
import { useFlywheelCycle } from "./useFlywheelCycle";
import {
  ClassOption,
  StepConfig,
} from "views/components/LabelPropagation/types";
import SeedStep, {
  SeedStepRef,
} from "views/components/LabelPropagation/SeedStep";
import { CardItem } from "views/components/LabelPropagation/SelectedClassesListStep/ClassCard";
import AddClassButton from "views/components/LabelPropagation/SeedStep/AddClassButton";
import ProgressStep, {
  LoadingType,
  showsMainLoader,
} from "views/components/LabelPropagation/ProgressStep";
import {
  VLButtonProps,
  VLPrimaryButtonProps,
} from "views/uikit/Buttons/VLButton";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  MANUAL_REVIEW_FLOW,
  REVIEW_PARAMETER,
  REVIEW_SORT_PARAMETER,
  REVIEW_TAB_PARAMETER,
  SEED_PARAMETER,
} from "../../helpers/constants/miscellaneous";
import { VLActionModalProps } from "views/uikit/VLModal/VLActionModal";
import {
  FlowStatus,
  usePollSeedStatusAndStats,
} from "./usePollSeedStatusAndStats";
import { toast } from "react-toastify";
import { VLNoActionModal } from "views/uikit/VLModal";
import { useStartSeed } from "./useStartSeed";
import { useGetSeed } from "./useGetSeed";
import { FlywheelStatus } from "../../vl-api/vlSchemas";
import { useRefetchFlywheelStatus } from "./useFlywheelStatus";
import { useSearchParams } from "react-router-dom";
import { useClearLabelPropagationSearchParams } from "./useClearLabelPropagationSearchParams";
import { isFinalReviewOpen, useFinalReviewSummary } from "./useFinalReview";
import { useApplyFinalReviewApiV1FlywheelDatasetIdFinalReviewFlowIdApplyPut } from "../../vl-api/vlComponents";
import VLToastComponent from "views/components/VLToastComponent";
import FinalReviewApplyContent from "views/components/LabelPropagation/FinalReviewApplyContent";
import { App } from "antd";

interface UseStepsConfigProps {
  selectedValues?: string[];
  setSelectedValues: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  options: ClassOption[];
  selectedCard: string | null;
  progressLimit: number;
  loading?: boolean;
  error?: any;
  allClassesMeetLimit: boolean;
  onDelete?: (value: string) => void;
  onItemDelete?: (value: string, mediaId: string) => void;
  classItemsMap?: Record<string, CardItem[]>;
  seedId?: string | null;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  datasetId: string;
  requiredSeedSamplesPerLabel: number;
  setModal: Dispatch<SetStateAction<VLActionModalProps | null>>;
  seedStepRef?: React.RefObject<SeedStepRef>;
  setClassItemsMap: React.Dispatch<
    React.SetStateAction<Record<string, CardItem[]>>
  >;
  shouldFetch?: string;
  resetShouldFetch: () => void;
  onLabelPropagationCompleted: () => void;
  flywheelStatus?: FlywheelStatus;
  showSummaryTab: () => void;
}

export const useStepsConfig = ({
  selectedValues,
  setSelectedValues,
  options,
  selectedCard,
  progressLimit,
  loading,
  error,
  allClassesMeetLimit,
  onDelete,
  onItemDelete,
  classItemsMap,
  setStep,
  datasetId,
  requiredSeedSamplesPerLabel,
  setModal,
  seedStepRef,
  setClassItemsMap,
  shouldFetch,
  resetShouldFetch,
  onLabelPropagationCompleted,
  flywheelStatus,
  showSummaryTab,
}: UseStepsConfigProps): StepConfig[] => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const seedId = searchParams.get(SEED_PARAMETER);
  const isReview = searchParams.get(REVIEW_PARAMETER);
  const [active, setActive] = useState<boolean>(false);

  useEffect(() => {
    switch (flywheelStatus) {
      case "RESUME_FLOW":
        setActive(true);
        setLoadingType("SUBMIT");
        break;
      case "INIT":
        setActive(true);
        setLoadingType("RUN");
        break;
      case "APPLY_MODEL_TO_DATASET":
        setActive(true);
        // keep "APPLY" when the labels are applied from the final review
        setLoadingType((prev) => prev ?? "FINISH");
        break;
      default:
        break;
    }
  }, [flywheelStatus, setActive]);

  const [startSeed, setStartSeed] = useState(false);

  const [doneAction, setDoneAction] = useState<() => void>();

  const [loadingType, setLoadingType] = useState<LoadingType>();
  const loadingTypeRef = useRef<LoadingType>();
  loadingTypeRef.current = loadingType;

  const isFinalReview = flywheelStatus === "FINAL_REVIEW";
  const isFinalReviewPage = isFinalReview && isFinalReviewOpen(searchParams);

  const [flowId, setFlowId] = useState(searchParams.get(FLOW_PARAMETER));

  const { fetchSeed, seed } = useGetSeed();

  const updateFlywheelStatus = useRefetchFlywheelStatus();

  const { status, isFinalStatus } = usePollSeedStatusAndStats({
    datasetId,
    flowId: flowId ?? null,
    seedId,
    active,
  });

  const { flowId: startedFlowId } = useStartSeed({
    datasetId: datasetId || "",
    seedId: seedId || "",
    active: startSeed && !flowId,
  });

  useEffect(() => {
    if (startedFlowId && !flowId) {
      setFlowId(startedFlowId);
      setActive(true);
      setStartSeed(false);
      if (!searchParams.has(FLOW_PARAMETER)) {
        searchParams.set(FLOW_PARAMETER, startedFlowId);
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, setSearchParams, startedFlowId, flowId]);

  const clearLabelPropagationSearchParams =
    useClearLabelPropagationSearchParams();

  const onAborted = useCallback(() => {
    clearLabelPropagationSearchParams();
    setModal(null);
    showSummaryTab();
  }, [clearLabelPropagationSearchParams, setModal, showSummaryTab]);

  const onFinished = () => {
    setLoadingType("FINISH");
    setActive(true);
    setModal(null);
  };

  const { runCycle, finishCycle, resetCycle } = useFlywheelCycle({
    onAborted,
    onFinished,
  });

  const { fetchStats, stats } = useGetStats();

  const { data: finalReviewSummary } = useFinalReviewSummary(datasetId, flowId);

  const displayReviewStepNextBtn =
    stats?.review.mainProgress &&
    stats.review.mainProgress.maxItems === stats.review.mainProgress.itemLength;

  useEffect(() => {
    seedId && fetchSeed(datasetId, seedId);
    flowId && fetchStats(datasetId, flowId);
  }, [seedId, datasetId, fetchSeed, flowId, fetchStats]);

  useEffect(() => {
    if (!active || !status) return;
    if (status === FlowStatus.PAUSED_FOR_REVIEW) {
      (async () => {
        try {
          if (flowId) await fetchStats(datasetId, flowId);
          const action = doneAction;
          if (action) action();
        } finally {
          setLoadingType(undefined);
          setActive(false);
          updateFlywheelStatus("PAUSED_FOR_REVIEW");
        }
      })();
    }
  }, [
    isFinalStatus,
    active,
    datasetId,
    flowId,
    fetchStats,
    doneAction,
    status,
    updateFlywheelStatus,
  ]);

  // Finish (or a failed apply) stops the flow at the final review
  useEffect(() => {
    if (!active || status !== FlowStatus.FINAL_REVIEW) return;
    const applyFailed = loadingTypeRef.current === "APPLY";
    setActive(false);
    setLoadingType(undefined);
    updateFlywheelStatus("FINAL_REVIEW");
    if (applyFailed) {
      toast(
        <VLToastComponent
          title="Apply labels failed"
          status="ERROR"
          description="The labels were not applied. Please try again."
        />,
      );
    }
  }, [active, status, updateFlywheelStatus]);

  useEffect(() => {
    if (!active || !status) return;

    if (status === FlowStatus.COMPLETED && flowId) {
      setActive(false);
      fetchStats(datasetId, flowId).then((res) => {
        setLoadingType(undefined);
        clearLabelPropagationSearchParams();
        const statsData = res as StatsData;
        const totalItemsText =
          statsData.stats.total_annotated === statsData.total_media_count
            ? "All"
            : statsData.stats.total_annotated;
        const toastId = toast(
          <VLNoActionModal
            isOpen={true}
            variant={"success"}
            onClose={() => toast.dismiss(toastId)}
            headers={{
              title: "Label Propagation Completed!",
              subTitle: `${totalItemsText} Images were successfully labeled in your dataset.`,
            }}
          />,
          { autoClose: 7000, closeButton: false, closeOnClick: false },
        );
        onLabelPropagationCompleted();
      });
    }
  }, [
    active,
    clearLabelPropagationSearchParams,
    datasetId,
    fetchStats,
    flowId,
    onLabelPropagationCompleted,
    status,
  ]);

  const onFinishClicked = useCallback(() => {
    setModal({
      onClick: async () => {
        if (datasetId && flowId) {
          finishCycle(datasetId, flowId);
        }
      },
      isOpen: true,
      variant: "info",
      onClose: () => setModal(null),
      headers: {
        title: "Ready to submit your feedback?",
        subTitle:
          "Once you submit, Label Propagation will stop and all labels annotated up to this point will be saved as final.",
      },
      actionLabel: "Yes, Finalize",
    });
  }, [setModal, datasetId, flowId, finishCycle]);

  const onAbortClicked = useCallback(() => {
    setModal({
      onClick: async () => {
        if (datasetId && flowId) {
          resetCycle(datasetId, flowId);
        }
      },
      isOpen: true,
      variant: "error",
      onClose: () => setModal(null),
      headers: {
        title: "All progress will be lost. Are you sure?",
        subTitle:
          "Resetting removes all annotations from Label Propagation. Any new labels or progress made will not be saved. You’ll return to the beginning.",
      },
      actionLabel: "Reset",
    });
  }, [datasetId, flowId, resetCycle, setModal]);

  const applyFinalReviewMutation =
    useApplyFinalReviewApiV1FlywheelDatasetIdFinalReviewFlowIdApplyPut({
      onSuccess: () => {
        setLoadingType("APPLY");
        setActive(true);
        updateFlywheelStatus("APPLY_MODEL_TO_DATASET");
        const params = new URLSearchParams(window.location.search);
        params.delete(LABEL_CLASS_PARAMETER);
        params.delete(REVIEW_PARAMETER);
        params.delete(REVIEW_TAB_PARAMETER);
        params.delete(REVIEW_SORT_PARAMETER);
        navigate({ pathname: location.pathname, search: params.toString() });
      },
      onError: (err: any) => {
        toast(
          <VLToastComponent
            title="Apply labels failed"
            status="ERROR"
            description={`Something went wrong ${err?.payload?.detail ?? ""}`}
          />,
        );
      },
    });
  const applyFinalReview = applyFinalReviewMutation.mutateAsync;

  const { modal } = App.useApp();

  const onApplyClicked = useCallback(() => {
    modal.confirm({
      title: "Apply labels to your dataset?",
      content: (
        <FinalReviewApplyContent
          labeled={finalReviewSummary?.labeled_count ?? 0}
          seed={finalReviewSummary?.seed_count ?? 0}
          changed={finalReviewSummary?.changed_count ?? 0}
          unlabeled={finalReviewSummary?.unlabeled_count ?? 0}
        />
      ),
      okText: "Apply labels",
      cancelText: "Keep reviewing",
      width: "fit-content",
      // the OK button shows a loader until the promise settles, then the modal closes
      onOk: async () => {
        if (!datasetId || !flowId) return;
        try {
          await applyFinalReview({ pathParams: { datasetId, flowId } });
        } catch {
          // reported by the mutation's onError
        }
      },
    });
  }, [modal, datasetId, flowId, applyFinalReview, finalReviewSummary]);

  const getProgressStepNextBtn =
    useCallback((): VLPrimaryButtonProps | null => {
      if (isFinalReview) {
        return {
          text: "Apply labels",
          type: "primary",
          isGradient: true,
          onClick: onApplyClicked,
          disabled: !!loadingType || !finalReviewSummary,
          tooltip:
            "Save these labels to your dataset and finish Label Propagation",
        };
      }
      if (isReview) {
        return {
          text: "Submit",
          type: "primary",
          isGradient: true,
          disabled: !displayReviewStepNextBtn || !!loadingType,
          tooltip: loadingType
            ? "Submitting..."
            : !displayReviewStepNextBtn
              ? "Review all images in this batch to submit"
              : "Send your feedback and run the next cycle",
          loading: !!loadingType,
          onClick: async () => {
            setLoadingType("SUBMIT");
            if (!flowId || !datasetId) return;

            setDoneAction(() => {});

            await runCycle(datasetId, flowId);
            fetchStats(datasetId, flowId);
            setActive(true);
            setLoadingType("SUBMIT");
            const params = new URLSearchParams(location.search);
            params.delete(LABEL_CLASS_PARAMETER);
            params.delete(REVIEW_PARAMETER);
            params.delete(REVIEW_TAB_PARAMETER);
            params.delete(MANUAL_REVIEW_FLOW);
            navigate({
              pathname: location.pathname,
              search: params.toString(),
            });
          },
        };
      }
      return {
        text: "Finish",
        type: "primary",
        onClick: onFinishClicked,
        disabled: !!loadingType,
        tooltip: "Stop Label Propagation and keep all labels assigned so far",
      };
    }, [
      isFinalReview,
      onApplyClicked,
      finalReviewSummary,
      isReview,
      onFinishClicked,
      displayReviewStepNextBtn,
      loadingType,
      flowId,
      datasetId,
      runCycle,
      fetchStats,
      location.search,
      location.pathname,
      navigate,
    ]);

  const getProgressStepPrevBtn = useCallback((): VLButtonProps | null => {
    if (!isReview || isFinalReview)
      return {
        text: "Reset",
        type: "default",
        onClick: onAbortClicked,
        disabled: !!loadingType,
        tooltip: "Delete all Label Propagation progress and start over",
      };
    return null;
  }, [onAbortClicked, isReview, isFinalReview, loadingType]);

  const getSeedStepNextBtn = useCallback(
    (): VLPrimaryButtonProps | null => ({
      text: "Start Label Propagation",
      type: "primary",
      disabled:
        !allClassesMeetLimit || (selectedValues && selectedValues?.length < 2),
      onClick: () => {
        setLoadingType("RUN");
        setStep(2);
        setStartSeed(true);
      },
      tooltip:
        selectedValues && selectedValues?.length < 2
          ? "Select at least two labels to continue"
          : !allClassesMeetLimit
            ? `Select at least ${requiredSeedSamplesPerLabel} items per label to continue`
            : undefined,
    }),
    [allClassesMeetLimit, selectedValues, requiredSeedSamplesPerLabel, setStep],
  );

  useEffect(() => {
    if (!flowId || !datasetId) return;
    shouldFetch && shouldFetch.length > 0 && fetchStats(datasetId, flowId);
    resetShouldFetch();
  }, [datasetId, fetchStats, flowId, shouldFetch, resetShouldFetch]);

  // Title/subtitle config based on whether any classes are selected
  const titleByMode: Record<
    "selected" | "default",
    { text: string; subText?: string }
  > = {
    selected: {
      text: "Add Samples",
    },
    default: {
      text: "Label Propagation",
      subText: "Propagate Labels Across Your Dataset",
    },
  };

  return [
    {
      key: 1,
      title: selectedValues
        ? {
            ...titleByMode[selectedValues.length > 0 ? "selected" : "default"],
            icon:
              selectedValues.length > 0 ? (
                <AddClassButton
                  onClick={() => seedStepRef?.current?.handleOpenModal()}
                />
              ) : undefined,
            count:
              classItemsMap && Object.keys(classItemsMap).length > 0
                ? Object.values(classItemsMap).reduce(
                    (total, items) => total + items.length,
                    0,
                  )
                : undefined,
          }
        : undefined,
      content: (
        <SeedStep
          ref={seedStepRef}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          options={options}
          loading={loading}
          error={error}
          progressLimit={progressLimit}
          selectedCard={selectedCard}
          onDelete={onDelete ?? (() => {})}
          onItemDelete={onItemDelete ?? (() => {})}
          classItemsMap={classItemsMap}
          setClassItemsMap={setClassItemsMap}
          seedId={seedId}
          datasetId={datasetId}
          displayAddClassButton={selectedValues?.length === 0}
        />
      ),
      nextButton: selectedValues?.length ? getSeedStepNextBtn() : null,
      prevButton: null,
    },
    {
      key: 2,
      title: isFinalReviewPage
        ? {
            text: "Final Review",
            subText:
              "Check the propagated labels before they are saved to your dataset",
            hideGuide: true,
          }
        : undefined,
      content: (
        <ProgressStep
          seedId={seedId}
          initialFlowId={flowId}
          stats={stats}
          loadingType={loadingType}
          seed={seed}
          pausedForReview={status === FlowStatus.PAUSED_FOR_REVIEW}
          isFinalReview={isFinalReview}
        />
      ),
      nextButton: getProgressStepNextBtn(),
      prevButton: getProgressStepPrevBtn(),
      showsMainLoader: showsMainLoader(loadingType),
    },
  ];
};
